import type { FilterSummary } from "@app/components/shared/filter_panel/filterState";
import { Button, Chip, cn, Plus } from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  AnimatePresence,
  domMax,
  LazyMotion,
  m,
  useReducedMotion,
} from "framer-motion";
import type { ReactNode } from "react";
import { useLayoutEffect, useRef } from "react";

function SummaryLabel({
  categoryLabel,
  options,
}: Pick<FilterSummary<string>, "categoryLabel" | "options">) {
  const optionNames = options.reduce<ReactNode>((previousNames, option) => {
    const optionName = <span className="font-bold">{option.name}</span>;
    return previousNames === null ? (
      optionName
    ) : (
      <Trans>
        {previousNames} or {optionName}
      </Trans>
    );
  }, null);

  return (
    <span className="min-w-0 truncate text-xs font-medium">
      <Trans>
        <span className="font-bold">{categoryLabel}</span> is {optionNames}
      </Trans>
    </span>
  );
}

// Shared by a preset and the active chip selecting exactly its options, so React keeps one element
// when the preset is applied or removed.
function getPresetChipKey({ category, options }: FilterSummary<string>) {
  return `preset:${category}:${options
    .map(({ id }) => id)
    .toSorted()
    .join(",")}`;
}

function isPresetApplied<Category extends string>(
  preset: FilterSummary<Category>,
  summaries: FilterSummary<Category>[]
) {
  const selectedIds = new Set(
    summaries
      .find(({ category }) => category === preset.category)
      ?.options.map(({ id }) => id)
  );
  return preset.options.every(({ id }) => selectedIds.has(id));
}

interface FilterExtraChip {
  key: string;
  label: ReactNode;
  onRemove: () => void;
}

interface FilterSummaryChipsProps<
  Category extends string,
  Preset extends FilterSummary<Category>,
> {
  summaries: FilterSummary<Category>[];
  // Chips for settings outside the filter categories, shown after the category chips.
  extraChips?: FilterExtraChip[];
  // Faded filters, labelled as the chip they become, applied on click. Shown before the active
  // chips, unless all their options are already selected.
  presets?: Preset[];
  onApplyPreset?: (preset: Preset) => void;
  onClearCategory: (category: Category) => void;
  onClearAll: () => void;
  isLoading?: boolean;
  className?: string;
}

export function FilterSummaryChips<
  Category extends string,
  Preset extends FilterSummary<Category>,
>({
  summaries,
  extraChips = [],
  presets = [],
  onApplyPreset,
  onClearCategory,
  onClearAll,
  isLoading = false,
  className,
}: FilterSummaryChipsProps<Category, Preset>) {
  const { t } = useLingui();
  const pendingFocus = useRef<{
    key?: string;
    fallback: HTMLButtonElement | null;
  } | null>(null);
  useLayoutEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) {
      return;
    }
    pendingFocus.current = null;
    const target = pending.key
      ? Array.from(
          document.querySelectorAll<HTMLElement>("[data-filter-chip-key]")
        )
          .find((element) => element.dataset.filterChipKey === pending.key)
          ?.querySelector<HTMLElement>('button, [role="button"]')
      : null;
    if (target?.isConnected) {
      target.focus({ preventScroll: true });
    } else if (pending.fallback?.isConnected) {
      pending.fallback.focus({ preventScroll: true });
    }
  });
  const presetKeys = new Set(presets.map(getPresetChipKey));
  const chips: FilterExtraChip[] = [
    ...summaries.map((summary) => ({
      key: presetKeys.has(getPresetChipKey(summary))
        ? getPresetChipKey(summary)
        : `category:${summary.category}`,
      label: (
        <SummaryLabel
          categoryLabel={summary.categoryLabel}
          options={summary.options}
        />
      ),
      onRemove: () => onClearCategory(summary.category),
    })),
    ...extraChips.map((chip) => ({ ...chip, key: `extra:${chip.key}` })),
  ];
  const chipsAndPresets = [
    ...presets
      .filter((preset) => !isPresetApplied(preset, summaries))
      .map((preset) => ({
        key: getPresetChipKey(preset),
        label: (
          <SummaryLabel
            categoryLabel={preset.categoryLabel}
            options={preset.options}
          />
        ),
        onRemove: undefined,
        onApply: () => onApplyPreset?.(preset),
      })),
    ...chips.map((chip) => ({ ...chip, onApply: undefined })),
  ];
  const shouldReduceMotion = useReducedMotion();
  const transition = shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.18, ease: "easeOut" as const };

  const prepareFocus = (key?: string) => {
    const element = document.activeElement?.closest<HTMLElement>(
      "[data-filter-chip-key]"
    );
    const scope = element?.closest('[role="dialog"], main') ?? document;
    const fallback =
      Array.from(scope.querySelectorAll<HTMLButtonElement>("button")).find(
        (button) => button.getAttribute("aria-label") === t`Filters`
      ) ?? null;
    pendingFocus.current = {
      key: key && presetKeys.has(key) ? key : undefined,
      fallback,
    };
  };

  return (
    <LazyMotion features={domMax}>
      <AnimatePresence initial={false}>
        {chipsAndPresets.length > 0 && (
          <m.div
            key="filter-summary-chips"
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={shouldReduceMotion ? undefined : { opacity: 0 }}
            transition={transition}
            className={className}
          >
            <div
              aria-busy={isLoading}
              className="flex flex-wrap items-center gap-2"
            >
              <AnimatePresence initial={false}>
                {chipsAndPresets.map((chip) => (
                  <m.div
                    key={chip.key}
                    data-filter-chip-key={chip.key}
                    layout={!shouldReduceMotion}
                    initial={shouldReduceMotion ? false : { opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={
                      shouldReduceMotion ? undefined : { opacity: 0, x: -4 }
                    }
                    transition={transition}
                    className="max-w-full [&>div]:min-h-11 [&_button]:min-h-11 [&_button]:min-w-11"
                  >
                    <Chip
                      size="xs"
                      color={chip.onApply ? "primary" : "highlight"}
                      className={cn(
                        "max-w-full border border-dashed",
                        "transition duration-200 motion-reduce:transition-none",
                        chip.onApply
                          ? "border-primary-300 opacity-90 hover:opacity-100"
                          : "border-transparent"
                      )}
                      icon={chip.onApply ? Plus : undefined}
                      onClick={
                        chip.onApply
                          ? () => {
                              prepareFocus(chip.key);
                              chip.onApply?.();
                            }
                          : undefined
                      }
                      onRemove={
                        chip.onRemove
                          ? () => {
                              prepareFocus(chip.key);
                              chip.onRemove?.();
                            }
                          : undefined
                      }
                    >
                      {chip.label}
                    </Chip>
                  </m.div>
                ))}
              </AnimatePresence>
              {chips.length > 0 && (
                <m.div layout={!shouldReduceMotion} transition={transition}>
                  <Button
                    label={t`Clear all`}
                    size="xs"
                    variant="ghost-secondary"
                    onClick={
                      onClearAll
                        ? () => {
                            prepareFocus();
                            onClearAll();
                          }
                        : undefined
                    }
                  />
                </m.div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
}
